import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import AppointmentsPage from './pages/AppointmentsPage';
import AdminApp from './admin/AdminApp';
import './index.css';

/* ─── Lazy-loaded public pages ─── */
const AboutPage       = React.lazy(() => import('./pages/AboutPage'));
const ServicesPage    = React.lazy(() => import('./pages/ServicesPage'));
const DoctorsPage     = React.lazy(() => import('./pages/DoctorsPage'));
const BlogPage        = React.lazy(() => import('./pages/BlogPage'));
const TestimonialsPage= React.lazy(() => import('./pages/TestimonialsPage'));
const ContactPage     = React.lazy(() => import('./pages/ContactPage'));

/* ─── Scroll to top on route change ─── */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [pathname]);
  return null;
};

/* ─── Scroll-to-top button ─── */
const ScrollBtn = () => {
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (isAdmin) return null;
  return (
    <button
      className={`scroll-to-top ${visible ? 'visible' : ''}`}
      id="scroll-to-top"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
    >
      ↑
    </button>
  );
};

/* ─── Loading fallback ─── */
const PageLoader = () => (
  <div className="spinner-overlay" id="page-loader">
    <div className="spinner" />
  </div>
);

/* ─── Public Layout Wrapper ─── */
const PublicLayout = ({ children }) => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  if (isAdmin) return children;
  return (
    <>
      <Navbar />
      <main id="main-content">{children}</main>
      <Footer />
    </>
  );
};

/* ─── App ─── */
function App() {
  return (
    <Router>
      <ScrollToTop />
      <React.Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ── Admin Panel Routes (no Navbar/Footer) ── */}
          <Route path="/admin/*" element={<AdminApp />} />

          {/* ── Public Website Routes ── */}
          <Route path="*" element={
            <PublicLayout>
              <Routes>
                <Route path="/"             element={<HomePage />} />
                <Route path="/about"        element={<AboutPage />} />
                <Route path="/services"     element={<ServicesPage />} />
                <Route path="/doctors"      element={<DoctorsPage />} />
                <Route path="/appointments" element={<AppointmentsPage />} />
                <Route path="/blog"         element={<BlogPage />} />
                <Route path="/testimonials" element={<TestimonialsPage />} />
                <Route path="/contact"      element={<ContactPage />} />
                <Route path="*" element={
                  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: '4rem 2rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>🏥</div>
                    <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '2.5rem', color: 'var(--color-navy)', marginBottom: '1rem' }}>Page Not Found</h1>
                    <p style={{ color: 'var(--color-text-muted)', marginBottom: '2rem' }}>The page you're looking for doesn't exist.</p>
                    <a href="/" className="btn btn-primary">Return to Home</a>
                  </div>
                } />
              </Routes>
            </PublicLayout>
          } />
        </Routes>
      </React.Suspense>
      <ScrollBtn />
    </Router>
  );
}

export default App;
